import { useNavigate } from "react-router-dom";
import { User, Phone, FileText, Percent, Edit3, UserPlus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPhoneNumber } from "@/lib/phone-utils";

interface DuplicatePatient {
  id: string;
  name: string;
  phone: string;
  patient_code?: string;
  registration_number?: string;
  similarity_score: number;
}

interface DuplicatePatientWarningProps {
  duplicatePatient: DuplicatePatient;
  onViewExisting: () => void;
  onCreateAnyway: () => void;
  onEditDetails: () => void;
}

export const DuplicatePatientWarning = ({
  duplicatePatient,
  onViewExisting,
  onCreateAnyway,
  onEditDetails,
}: DuplicatePatientWarningProps) => {
  const matchPercent = Math.round(duplicatePatient.similarity_score * 100);
  const displayCode = duplicatePatient.patient_code || duplicatePatient.registration_number || "N/A";

  return (
    <div className="space-y-4">
      <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <User className="h-5 w-5" />
            Similar Patient Found
          </CardTitle>
          <CardDescription className="text-amber-700 dark:text-amber-300">
            We found a patient with matching details. Please verify before proceeding.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing Patient Details Card */}
          <div className="rounded-lg border bg-background p-4 space-y-2">
            <p className="font-semibold text-lg">{duplicatePatient.name}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                {formatPhoneNumber(duplicatePatient.phone)}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                {displayCode}
              </span>
              <span className="flex items-center gap-1">
                <Percent className="h-3.5 w-3.5" />
                {matchPercent}% name match
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button 
              onClick={onViewExisting} 
              className="w-full" 
              size="lg"
            >
              <User className="h-4 w-4 mr-2" />
              View Existing Patient
            </Button>
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-2">
                This is a different person with similar details?
              </p>
              <Button 
                onClick={onCreateAnyway} 
                variant="outline" 
                className="w-full"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create New Patient
              </Button>
            </div>
            
            <Button 
              onClick={onEditDetails} 
              variant="ghost" 
              className="w-full text-muted-foreground"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Details
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
